import type {
  GetAssetProofRpcResponse,
  GetAssetsByOwnerRpcInput,
  Metadata,
  Mint,
  NftOriginalEdition,
  ReadApiAsset,
  ReadApiAssetList,
  SplTokenCurrency,
} from "@metaplex-foundation/js";
import { amount, Pda, toBigNumber } from "@metaplex-foundation/js";
import { PROGRAM_ID as BUBBLEGUM_PROGRAM_ID } from "@metaplex-foundation/mpl-bubblegum";
import { TokenStandard } from "@metaplex-foundation/mpl-token-metadata";
import { PublicKey } from "@solana/web3.js";
import BN from "bn.js";

import { getMetadata } from "../../models/metaplex";
import { fetchJSONFromUrl } from "../utils";

/**
 * Convert a ReadApi asset (e.g. compressed NFT) into an NftEdition
 */
export const toNftEditionFromReadApiAsset = (input: ReadApiAsset): NftOriginalEdition => {
  return {
    model: "nftEdition",
    isOriginal: true,
    address: new PublicKey(input.id),
    supply: toBigNumber(input.supply?.print_current_supply ?? 0),
    maxSupply: toBigNumber(input.supply?.print_max_supply ?? 0),
  };
};

/**
 * Convert a ReadApi asset (e.g. compressed NFT) into an NFT mint
 */
export const toMintFromReadApiAsset = (input: ReadApiAsset): Mint => {
  const currency: SplTokenCurrency = {
    symbol: "Token",
    decimals: 0,
    namespace: "spl-token",
  };

  return {
    model: "mint",
    address: new PublicKey(input.id),
    mintAuthorityAddress: new PublicKey(input.id),
    freezeAuthorityAddress: new PublicKey(input.id),
    decimals: 0,
    supply: amount(1, currency),
    isWrappedSol: false,
    currency,
  };
};

function getAssetId(input: ReadApiAsset) {
  return Pda.find(BUBBLEGUM_PROGRAM_ID, [
    Buffer.from("asset", "utf-8"),
    new PublicKey(input.compression.tree).toBuffer(),
    Uint8Array.from(new BN(input.compression.leaf_id).toArray("le", 8)),
  ]);
}

/**
 * Convert a ReadApi asset's data into standard Metaplex `Metadata`
 */
export const toMetadataFromReadApiAsset = (input: ReadApiAsset): Metadata => {
  const updateAuthority = input.authorities?.find((authority) => authority.scopes.includes("full"));

  const collection = input.grouping.find(({ group_key }) => group_key === "collection");

  const address = input.compression.compressed ? getAssetId(input) : (getMetadata(new PublicKey(input.id)) as Pda);

  // Sometimes ReadApi deosn't return json metadata, so we set loaded to false so it can be fetched client-side.
  const isJsonIndexed = !!input.content.metadata?.description;

  return {
    model: "metadata",
    /**
     * We technically don't have a metadata address anymore.
     * So we are using the asset's id as the address
     */
    address,
    mintAddress: new PublicKey(input.id),
    updateAuthorityAddress: new PublicKey(updateAuthority!.address),

    name: input.content.metadata?.name ?? "",
    symbol: input.content.metadata?.symbol ?? "",

    json: { ...input.content.metadata, ...input.content["links"] },
    jsonLoaded: isJsonIndexed ? true : false,
    uri: input.content.json_uri,
    isMutable: input.mutable,

    primarySaleHappened: input.royalty.primary_sale_happened,
    sellerFeeBasisPoints: input.royalty.basis_points,
    creators: input.creators,

    editionNonce: input.supply?.edition_nonce ?? null,
    tokenStandard: TokenStandard.NonFungible,

    collection: collection ? { address: new PublicKey(collection.group_value), verified: false } : null,

    // Current regular `Metadata` does not currently have a `compression` value
    compression: input.compression,

    // Read API doesn't return this info, yet
    collectionDetails: null,
    // Read API doesn't return this info, yet
    uses: null,
    // Read API doesn't return this info, yet
    programmableConfig: null,
  };
};

interface ReadApiResponse<T> {
  id: string;
  jsonrpc: string;
  result: T;
}

export const getAssetsByOwner = async (endpoint: string, params: GetAssetsByOwnerRpcInput) => {
  const { ownerAddress, after, before, limit, page, sortBy } = params;
  const jsonData: ReadApiResponse<ReadApiAssetList> = await fetchJSONFromUrl(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      jsonrpc: "2.0",
      method: "getAssetsByOwner",
      id: "rpd-op-123",
      params: {
        ownerAddress,
        after: after ?? null,
        before: before ?? null,
        limit: limit ?? null,
        page: page ?? 1,
        sortBy: sortBy ?? null,
      },
    }),
  });
  return jsonData.result;
};

export const getAssetProof = async (endpoint: string, assetId: PublicKey) => {
  const jsonData: ReadApiResponse<GetAssetProofRpcResponse> = await fetchJSONFromUrl(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      jsonrpc: "2.0",
      method: "getAssetProof",
      id: "rpd-op-123",
      params: {
        id: assetId.toBase58(),
      },
    }),
  });
  return jsonData.result;
};
